import { rem, transparentize } from 'polished';
import React, { MouseEvent, ReactElement, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { zIndex } from 'styles';
import { Dots, Hamburger } from 'icons';
import { isMSTeams } from 'utils/MSTeams';
import { BodyText, extraSmall } from '@unmind/design-system';
import { useQuery } from '@apollo/client';
import { USER_QUERY } from 'Assistant/Chatbot';
import { userQuery } from 'Services/User/__generated__/userQuery';
import {
  VERTICAL_MENU_SIDE_PADDING,
  VERTICAL_MENU_WIDTH,
} from 'Navigation/VerticalMenu/VerticalMenu';

const Wrapper = styled.div<{ $isInNovaLUI: boolean }>`
  position: relative;
  height: 100%;
  z-index: ${zIndex.dropdown};
  display: flex;

  ${({ $isInNovaLUI }) =>
    !$isInNovaLUI &&
    css`
      ${extraSmall(css`
        align-items: end;
        flex-direction: row;
      `)}
    `}
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ProfilePicture = styled.div`
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: ${rem(4)};
  aspect-ratio: 1/1;
  background-color: ${({ theme }) => theme.colors.background.inverse};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${rem(8)};
`;

const ProfilePictureText = styled(BodyText).attrs(({ theme }) => ({
  sizes: [theme.typography.fontSizes.fontSize16],
  weight: theme.typography.fontWeights.semiBold,
  color: theme.colors.text.inverse,
}))``;

const UserName = styled(BodyText).attrs(({ theme }) => ({
  sizes: [
    theme.typography.fontSizes.fontSize12,
    theme.typography.fontSizes.fontSize14,
    theme.typography.fontSizes.fontSize16,
  ],
  weight: theme.typography.fontWeights.medium,
  color: theme.colors.text.primary,
}))`
  margin-right: ${rem(8)};
`;

const DotsIcon = styled(Dots)`
  transform: rotate(90deg);
`;

const BurgerIcon = styled(Hamburger).attrs(({ theme }) => ({
  primaryColor: theme.colors.text.primary,
  secondaryColor: 'transparent',
  width: 26,
  height: 17,
}))``;

const DropdownButton = styled.button<{
  $isOpen: boolean;
  $isInNovaLUI?: boolean;
  $isInVerticalMenu: boolean;
}>`
  border: none;
  cursor: pointer;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};

  ${({ $isInNovaLUI, theme, $isInVerticalMenu, $isOpen }) =>
    $isInNovaLUI
      ? css`
          border-radius: 100px;
        `
      : css`
          ${extraSmall(css`
            border: none;
            cursor: pointer;
            background-color: transparent;
            color: ${theme.colors.text.secondary};
            min-width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: ${rem(8)};
            border-radius: 8px;

            &:hover {
              background-color: ${theme.colors.background.primary};
            }
          `)};

          ${$isInVerticalMenu &&
          $isOpen &&
          css`
            background-color: ${theme.colors.background.primary};
          `}
        `}
`;

const DropdownMenuPopover = styled.div<{
  $isOpen: boolean;
  $isInNovaLUI: boolean;
  $isInVerticalMenu: boolean;
}>`
  background-color: ${({ theme }) => theme.colors.background.card};
  position: absolute;
  transform: translateY(${({ $isOpen }) => ($isOpen ? '100%' : '98%')});
  bottom: -8px;
  right: 0px;
  transition: all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  min-width: ${isMSTeams() ? rem(270) : rem(240)};
  box-shadow:
    0px 8px 16px -4px
      ${({ theme }) => transparentize(0.06, theme.colors.boxShadow)},
    0px 16px 32px -8px
      ${({ theme }) => transparentize(0.06, theme.colors.boxShadow)};
  border-radius: 12px;

  ${({ $isInNovaLUI, $isOpen, theme, $isInVerticalMenu }) =>
    $isInNovaLUI
      ? css`
          box-shadow: ${`0px 16px 32px -8px ${theme.colors.border.secondary}`};
          min-width: ${rem(220)};
        `
      : css`
          display: ${$isOpen ? 'flex' : 'none'};
          ${$isInVerticalMenu
            ? extraSmall(css`
                position: absolute;
                transform: none;
                bottom: 0px;
                left: ${rem(VERTICAL_MENU_WIDTH - VERTICAL_MENU_SIDE_PADDING)};
              `)
            : extraSmall(css`
                position: relative;
                transform: none;
                bottom: 0px;
                right: 0px;
              `)}
        `}
`;

const DropdownMenuList = styled.ul`
  padding: ${rem(8)} 0 ${rem(6)};
  margin: 0;
  list-style-type: none;
  width: 100%;

  li {
    padding: 0px 16px;
  }

  li:nth-last-child(2) {
    padding-bottom: ${rem(10)};
  }

  li:last-child {
    padding-top: ${rem(8)};
    border-top: 1px solid ${({ theme }) => theme.colors.border.secondary};
  }

  a {
    font-weight: inherit;
    text-decoration: none;

    &:hover {
      color: inherit;
    }
  }
`;

const MenuItemWrapper = styled.button<{
  $isInNovaLUI: boolean;
}>`
  display: block;
  text-align: left;
  border: none;
  cursor: pointer;
  padding: ${rem(12)} 0;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  width: 100%;

  ${props =>
    props.$isInNovaLUI &&
    css`
      padding: 8px 0;
      color: ${({ theme }) => theme.colors.text.primary};
    `};
`;

export interface MenuItemProps {
  parentMenuIsOpen?: boolean;
  label: React.ReactNode;
  onClick(event: MouseEvent<HTMLButtonElement>): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  isInNovaLUI?: boolean;
}

export const MenuItem = ({
  label,
  parentMenuIsOpen,
  isInNovaLUI = false,
  ...props
}: MenuItemProps) => (
  <MenuItemWrapper
    tabIndex={parentMenuIsOpen ? 0 : -1}
    role="menuitem"
    $isInNovaLUI={isInNovaLUI}
    {...props}
  >
    {label}
  </MenuItemWrapper>
);

export interface DropdownMenuProps {
  children: ReactElement<MenuItemProps> | ReactElement<MenuItemProps>[];
  open: boolean;
  onClick(event: MouseEvent<HTMLButtonElement>): void;
  onRequestClose(): void;
  icon?: ReactElement;
  isInNovaLUI?: boolean;
  isInVerticalMenu: boolean;
}

export const DropdownMenu = ({
  children,
  onRequestClose,
  open,
  icon,
  isInNovaLUI = false,
  isInVerticalMenu,
  ...props
}: DropdownMenuProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { t: translate } = useTranslation('navigation');
  const { data } = useQuery<userQuery>(USER_QUERY);

  const profileName = data?.user?.firstName
    ? `${data.user.firstName} ${data.user.lastName}`
    : 'User';

  const handleClickOutside = (event: Event) => {
    const target = event.target as HTMLDivElement;
    if (wrapperRef.current && !wrapperRef.current.contains(target)) {
      onRequestClose();
    }
  };

  const handleKeyboardClose = (event: KeyboardEvent): void => {
    const escKeyPressed = event.key === 'Esc' || event.key === 'Escape';
    if (open && escKeyPressed) {
      onRequestClose();
    }

    return;
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false);

    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardClose, {
      capture: true,
    });

    return () => {
      document.removeEventListener('keydown', handleKeyboardClose, {
        capture: true,
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    /**
     * *  `wrapperRef` is cast to any as a workaround to a Typescript & Styled-Components issue:
     * *  https://github.com/DefinitelyTyped/DefinitelyTyped/issues/28884
     * *  https://spectrum.chat/styled-components/general/typescript-refs~5857d917-966e-4a71-940f-524206896f43
     */
    <Wrapper ref={wrapperRef} $isInNovaLUI={isInNovaLUI}>
      <DropdownButton
        $isOpen={open}
        $isInVerticalMenu={isInVerticalMenu}
        aria-label={translate('dropdown_menu.dropdown_button.a11y_label')}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="dropdown-menu"
        id="dropdown-menu-trigger"
        $isInNovaLUI={isInNovaLUI}
        {...props}
      >
        {isInVerticalMenu ? (
          <>
            <ProfileContainer>
              <ProfilePicture>
                <ProfilePictureText>{profileName.charAt(0)}</ProfilePictureText>
              </ProfilePicture>
              <UserName>{profileName}</UserName>
            </ProfileContainer>
            <DotsIcon primaryColor="currentColor" width={20} height={20} />
          </>
        ) : icon ? (
          icon
        ) : (
          <BurgerIcon />
        )}
      </DropdownButton>
      <DropdownMenuPopover
        $isOpen={open}
        aria-hidden={!open}
        $isInNovaLUI={isInNovaLUI}
        $isInVerticalMenu={isInVerticalMenu}
      >
        <DropdownMenuList role="menu" aria-labelledby="dropdown-menu-trigger">
          {React.Children.map(children, child => (
            <li role="presentation">
              {React.cloneElement(child, {
                ...child.props,
                parentMenuIsOpen: open,
                onClick: (event: MouseEvent<HTMLButtonElement>) => {
                  onRequestClose();
                  child.props.onClick(event);
                },
              })}
            </li>
          ))}
        </DropdownMenuList>
      </DropdownMenuPopover>
    </Wrapper>
  );
};
